import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../services/apiClient';
import './PhoneScreeningModal.css';

const EMPTY_FORM = {
    call_at: '',
    recruiter_name: '',
    duration_minutes: 30,
    score: '',
    notes: '',
    result: 'pass',
    checklist: {
        communication: null,
        role_fit: null,
        notice_period: '',
        expected_ctc: '',
        availability: '',
    },
};

function toForm(record) {
    if (!record) return { ...EMPTY_FORM, checklist: { ...EMPTY_FORM.checklist } };
    return {
        call_at: record.call_at || '',
        recruiter_name: record.recruiter_name || '',
        duration_minutes: record.duration_minutes ?? 30,
        score: record.score ?? '',
        notes: record.notes || '',
        result: record.result || 'pass',
        checklist: {
            communication: record.checklist?.communication ?? null,
            role_fit: record.checklist?.role_fit ?? null,
            notice_period: record.checklist?.notice_period || '',
            expected_ctc: record.checklist?.expected_ctc || '',
            availability: record.checklist?.availability || '',
        },
    };
}

function extractScreeningQuestions(questionnaire) {
    if (!questionnaire) return [];
    if (typeof questionnaire === 'string') {
        try {
            return extractScreeningQuestions(JSON.parse(questionnaire));
        } catch {
            return questionnaire.trim() ? [{ category: 'General', question: questionnaire.trim() }] : [];
        }
    }
    if (Array.isArray(questionnaire?.questions)) {
        return questionnaire.questions
            .map((item) => {
                if (typeof item === 'string') return { category: 'General', question: item };
                const question = (item?.question || item?.text || '').trim();
                if (!question) return null;
                return {
                    category: (item?.category || item?.topic || 'General').trim() || 'General',
                    question,
                };
            })
            .filter(Boolean);
    }
    // Legacy full interview questionnaire — surface intro/behavioral only as readable lines
    const legacy = [];
    const pushList = (list, category) => {
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
            const q = typeof item === 'string' ? item : item?.question;
            if (q) legacy.push({ category, question: String(q) });
        });
    };
    pushList(questionnaire.introduction, 'Introduction');
    pushList(questionnaire.behavioral, 'Behavioral');
    if (legacy.length) return legacy;
    return [];
}

const TriState = ({ label, value, onChange, disabled }) => (
    <label className="phone-screen-field">
        <span>{label}</span>
        <select
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={(e) => {
                const v = e.target.value;
                onChange(v === 'yes' ? true : v === 'no' ? false : null);
            }}
            disabled={disabled}
        >
            <option value="">Not assessed</option>
            <option value="yes">Yes / Pass</option>
            <option value="no">No / Fail</option>
        </select>
    </label>
);

const PhoneScreeningModal = ({ isOpen, onClose, candidate, jobId, onSaved }) => {
    const [form, setForm] = useState(() => ({ ...EMPTY_FORM, checklist: { ...EMPTY_FORM.checklist } }));
    const [meta, setMeta] = useState({ status: 'not_started', result: null, application_status: null });
    const [scheduleEmail, setScheduleEmail] = useState('');
    const [questionnaire, setQuestionnaire] = useState(null);
    const [questionPrompt, setQuestionPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sendingSlots, setSendingSlots] = useState(false);
    const [generatingQuestions, setGeneratingQuestions] = useState(false);
    const [transcriptText, setTranscriptText] = useState('');
    const [evaluation, setEvaluation] = useState(null);
    const [evaluatingTranscript, setEvaluatingTranscript] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Pass/Fail lock the form; Hold stays editable so recruiters can change to Pass later.
    const locked =
        meta.status === 'completed' && (meta.result === 'pass' || meta.result === 'fail');
    const onHold = meta.status === 'on_hold' || meta.result === 'hold';
    const inviteSent = meta.status === 'invite_sent';
    const scheduled = meta.status === 'scheduled';
    const resumeId = candidate?.id;
    const resolvedJobId = jobId || candidate?.job_id;
    const screeningQuestions = extractScreeningQuestions(questionnaire);

    useEffect(() => {
        if (!isOpen) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !resumeId) return undefined;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setSuccess(null);
            try {
                const res = await apiFetch(`/api/resumes/${resumeId}/phone-screening`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(data.detail || data.error || 'Failed to load phone screening');
                }
                if (cancelled) return;
                setForm(toForm(data.phone_screening));
                setScheduleEmail(data.phone_screening?.recruiter_name || '');
                setQuestionnaire(data.phone_screening?.questionnaire || null);
                setQuestionPrompt(data.phone_screening?.question_prompt || '');
                setEvaluation(data.phone_screening?.evaluation || null);
                setTranscriptText(data.phone_screening?.evaluation_payload?.transcript_text || '');
                setMeta({
                    status: data.phone_screening?.status || 'not_started',
                    result: data.phone_screening?.result || null,
                    application_status: data.application_status,
                    completed_at: data.phone_screening?.completed_at,
                });
            } catch (e) {
                if (!cancelled) setError(e.message || 'Failed to load recruiter screening');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isOpen, resumeId]);

    const updateField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const updateChecklist = (name, value) => {
        setForm((prev) => ({
            ...prev,
            checklist: { ...prev.checklist, [name]: value },
        }));
    };

    const payload = () => ({
        call_at: form.call_at || null,
        recruiter_name: form.recruiter_name,
        duration_minutes: form.duration_minutes === '' ? null : Number(form.duration_minutes),
        score: form.score === '' ? null : Number(form.score),
        notes: form.notes,
        result: form.result,
        checklist: form.checklist,
    });

    const handleSendSlots = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!resumeId || !resolvedJobId || locked || sendingSlots) return;
        if (!(scheduleEmail || '').trim()) {
            setError('Recruiter email is required to send slot options');
            return;
        }
        setSendingSlots(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await apiFetch('/api/recruiter-screening/schedule', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_id: resumeId,
                    job_id: Number(resolvedJobId),
                    recruiter_email: scheduleEmail.trim(),
                    duration_minutes: Number(form.duration_minutes) || 30,
                    notes: form.notes || '',
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || data.error || 'Failed to send slot options');
            }
            setMeta((prev) => ({ ...prev, status: 'invite_sent' }));
            setSuccess(data.message || 'Slot options sent to candidate.');
            if (onSaved) onSaved(data);
        } catch (err) {
            setError(err.message || 'Failed to send slot options');
        } finally {
            setSendingSlots(false);
        }
    };

    const handleGenerateQuestions = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!resumeId || locked || generatingQuestions) return;
        setGeneratingQuestions(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await apiFetch('/api/recruiter-screening/generate-questions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_id: resumeId,
                    force_new: true,
                    custom_prompt: (questionPrompt || '').trim(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || data.error || 'Failed to generate questions');
            }
            setQuestionnaire(data.questionnaire || null);
            if (typeof data.question_prompt === 'string') {
                setQuestionPrompt(data.question_prompt);
            }
            setSuccess(
                (questionPrompt || '').trim()
                    ? 'Questions generated using your custom prompt.'
                    : 'Recruiter screening questions ready. You may ask these or different questions on the call.'
            );
            if (onSaved) onSaved(data);
        } catch (err) {
            setError(err.message || 'Failed to generate questions');
        } finally {
            setGeneratingQuestions(false);
        }
    };

    const handleEvaluateTranscript = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!resumeId || locked || evaluatingTranscript) return;
        if (!(transcriptText || '').trim()) {
            setError('Paste the call transcript before evaluating.');
            return;
        }
        setEvaluatingTranscript(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await apiFetch('/api/recruiter-screening/evaluate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_id: resumeId,
                    transcript_text: transcriptText.trim(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || data.error || 'Failed to evaluate transcript');
            }
            const scorecard = data.evaluation || data.phone_screening?.evaluation || null;
            setEvaluation(scorecard);
            if (scorecard?.overall_score != null) {
                setForm((prev) => ({ ...prev, score: scorecard.overall_score }));
            }
            setSuccess(
                data.message ||
                    'AI scorecard ready. Review it, then set Pass / Hold / Fail to complete.'
            );
            if (onSaved) onSaved(data);
        } catch (err) {
            setError(err.message || 'Failed to evaluate transcript');
        } finally {
            setEvaluatingTranscript(false);
        }
    };

    const handleSaveDraft = async (e) => {
        e.preventDefault();
        if (!resumeId || locked) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await apiFetch(`/api/resumes/${resumeId}/phone-screening`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload()),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || data.error || 'Failed to save draft');
            }
            setForm(toForm(data.phone_screening));
            setMeta({
                status: data.phone_screening?.status || 'draft',
                result: data.phone_screening?.result || null,
                application_status: data.application_status,
                completed_at: data.phone_screening?.completed_at,
            });
            setSuccess('Draft saved. Candidate is in Recruiter screening.');
            if (onSaved) onSaved(data);
        } catch (err) {
            setError(err.message || 'Failed to save draft');
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = async (e) => {
        e.preventDefault();
        if (!resumeId || locked) return;
        if (!form.result) {
            setError('Select a result: Pass, Hold, or Fail');
            return;
        }
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await apiFetch(`/api/resumes/${resumeId}/phone-screening/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload()),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || data.error || 'Failed to complete recruiter screening');
            }
            setForm(toForm(data.phone_screening));
            setMeta({
                status: data.phone_screening?.status || 'completed',
                result: data.phone_screening?.result || null,
                application_status: data.application_status,
                completed_at: data.phone_screening?.completed_at,
            });
            const resultLabel = (data.phone_screening?.result || '').toUpperCase();
            if (data.phone_screening?.result === 'hold') {
                setSuccess(
                    `Recruiter screening on hold (${resultLabel}). Candidate stays in Recruiter screening — you can change to Pass later.`
                );
            } else if (data.phone_screening?.result === 'fail') {
                const mail = data.rejection_email;
                if (mail?.status === 'sent') {
                    setSuccess(
                        `Recruiter screening failed (${resultLabel}). Rejection email sent. Status: ${data.application_status}`
                    );
                } else if (mail?.status === 'skipped') {
                    setSuccess(
                        `Recruiter screening failed (${resultLabel}). Rejection email skipped (${mail.reason || 'no email'}). Status: ${data.application_status}`
                    );
                } else if (mail?.status === 'error') {
                    setSuccess(
                        `Recruiter screening failed (${resultLabel}). Status updated, but rejection email failed to send.`
                    );
                } else {
                    setSuccess(
                        `Recruiter screening failed (${resultLabel}). Status: ${data.application_status}`
                    );
                }
            } else {
                setSuccess(`Recruiter screening completed (${resultLabel}). Status: ${data.application_status}`);
            }
            if (onSaved) onSaved(data);
        } catch (err) {
            setError(err.message || 'Failed to complete recruiter screening');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="phone-screen-overlay" role="dialog" aria-modal="true">
            <div className="phone-screen-modal">
                <div className="phone-screen-header">
                    <h3>
                        Recruiter Screening — {candidate?.candidate_name || candidate?.filename || 'Candidate'}
                    </h3>
                    <button type="button" className="phone-screen-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="phone-screen-body">
                    {error && <div className="phone-screen-alert error">{error}</div>}
                    {success && <div className="phone-screen-alert success">{success}</div>}
                    {loading ? (
                        <p className="phone-screen-hint">Loading…</p>
                    ) : (
                        <>
                            {(inviteSent || scheduled) && !locked ? (
                                <div className="phone-screen-schedule-block phone-screen-invite-status">
                                    <h4 className="phone-screen-hint">Slot options</h4>
                                    <div
                                        className={`phone-screen-status-banner ${
                                            scheduled ? 'is-scheduled' : 'is-invite-sent'
                                        }`}
                                    >
                                        <span className="phone-screen-status-dot" aria-hidden="true" />
                                        <div>
                                            <strong>
                                                {scheduled
                                                    ? 'Recruiter screening scheduled'
                                                    : 'Slot options emailed'}
                                            </strong>
                                            <p>
                                                {scheduled
                                                    ? 'The candidate booked a slot. Complete the call notes and Pass / Hold / Fail below when done.'
                                                    : 'Invite sent — recruiter screening is waiting for the candidate to pick a time.'}
                                            </p>
                                            {(scheduleEmail || form.duration_minutes) && (
                                                <p className="phone-screen-status-meta">
                                                    {scheduleEmail ? `Sent via ${scheduleEmail}` : null}
                                                    {scheduleEmail && form.duration_minutes ? ' · ' : null}
                                                    {form.duration_minutes
                                                        ? `${form.duration_minutes} min`
                                                        : null}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : !locked ? (
                                <div className="phone-screen-schedule-block">
                                    <h4 className="phone-screen-hint">Send slot options</h4>
                                    <div className="phone-screen-grid">
                                        <label className="phone-screen-field">
                                            <span>Recruiter email (Teams calendar)</span>
                                            <input
                                                type="email"
                                                value={scheduleEmail}
                                                onChange={(e) => setScheduleEmail(e.target.value)}
                                                disabled={sendingSlots}
                                                placeholder="recruiter@company.com"
                                            />
                                        </label>
                                        <label className="phone-screen-field">
                                            <span>Duration (minutes)</span>
                                            <input
                                                type="number"
                                                min={15}
                                                max={120}
                                                value={form.duration_minutes}
                                                onChange={(e) => updateField('duration_minutes', e.target.value)}
                                                disabled={sendingSlots}
                                            />
                                        </label>
                                    </div>
                                    <button
                                        type="button"
                                        className="phone-screen-btn primary"
                                        onClick={handleSendSlots}
                                        disabled={sendingSlots || !resolvedJobId}
                                    >
                                        {sendingSlots ? 'Sending…' : 'Send slot options'}
                                    </button>
                                </div>
                            ) : null}

                            <div className="phone-screen-schedule-block">
                                <h4 className="phone-screen-hint">AI recruiter screening questions</h4>
                                <p className="phone-screen-hint">
                                    Leave the prompt blank for default screening questions, or add your own focus
                                    (e.g. notice period, domain experience). Soft skills and logistics stay included.
                                </p>
                                {!locked && (
                                    <>
                                        <label className="phone-screen-field">
                                            <span>Custom prompt (optional)</span>
                                            <textarea
                                                rows={3}
                                                value={questionPrompt}
                                                onChange={(e) => setQuestionPrompt(e.target.value)}
                                                disabled={generatingQuestions}
                                                placeholder="Example: Focus on Azure experience, 60-day notice, and willingness to travel."
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className="phone-screen-btn secondary"
                                            onClick={handleGenerateQuestions}
                                            disabled={generatingQuestions}
                                        >
                                            {generatingQuestions
                                                ? 'Generating…'
                                                : questionnaire
                                                  ? 'Regenerate questions'
                                                  : 'Generate AI questions'}
                                        </button>
                                    </>
                                )}
                                {screeningQuestions.length > 0 ? (
                                    <ol className="phone-screen-questions-list">
                                        {screeningQuestions.map((item, idx) => (
                                            <li key={`${idx}-${item.question.slice(0, 32)}`}>
                                                <span className="phone-screen-q-num">{idx + 1}.</span>
                                                <div className="phone-screen-q-body">
                                                    <span className="phone-screen-q-category">{item.category}</span>
                                                    <span className="phone-screen-q-text">{item.question}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                ) : questionnaire ? (
                                    <p className="phone-screen-hint">
                                        Saved questions are in an old format. Click Regenerate to refresh the list.
                                    </p>
                                ) : null}
                            </div>

                            <div className="phone-screen-schedule-block">
                                <h4 className="phone-screen-hint">Transcript → AI scorecard</h4>
                                <p className="phone-screen-hint">
                                    Paste the screening call transcript. AI returns an advisory scorecard —
                                    you still choose Pass / Hold / Fail below.
                                </p>
                                <label className="phone-screen-field">
                                    <span>Call transcript</span>
                                    <textarea
                                        rows={6}
                                        value={transcriptText}
                                        onChange={(e) => setTranscriptText(e.target.value)}
                                        disabled={locked || evaluatingTranscript}
                                        placeholder="Paste the recruiter screening transcript here…"
                                    />
                                </label>
                                {!locked && (
                                    <button
                                        type="button"
                                        className="phone-screen-btn secondary"
                                        onClick={handleEvaluateTranscript}
                                        disabled={evaluatingTranscript || !(transcriptText || '').trim()}
                                    >
                                        {evaluatingTranscript ? 'Evaluating…' : 'Evaluate with AI'}
                                    </button>
                                )}
                                {evaluation && (
                                    <div className="phone-screen-scorecard">
                                        <div className="phone-screen-scorecard-header">
                                            <span className="phone-screen-scorecard-score">
                                                {evaluation.overall_score != null
                                                    ? `${evaluation.overall_score}/100`
                                                    : '—'}
                                            </span>
                                            <span
                                                className={`phone-screen-scorecard-rec rec-${String(
                                                    evaluation.recommendation || 'hold'
                                                ).toLowerCase()}`}
                                            >
                                                Suggested: {(evaluation.recommendation || 'hold').toUpperCase()}
                                            </span>
                                        </div>
                                        {evaluation.summary && (
                                            <p className="phone-screen-scorecard-summary">{evaluation.summary}</p>
                                        )}
                                        {Array.isArray(evaluation.dimensions) && evaluation.dimensions.length > 0 && (
                                            <ul className="phone-screen-scorecard-dims">
                                                {evaluation.dimensions.map((dim) => (
                                                    <li key={dim.name}>
                                                        <strong>{dim.name}</strong>
                                                        <span>{dim.score}/100</span>
                                                        {dim.notes ? <em>{dim.notes}</em> : null}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {(evaluation.strengths?.length > 0 || evaluation.risks?.length > 0) && (
                                            <div className="phone-screen-scorecard-lists">
                                                {evaluation.strengths?.length > 0 && (
                                                    <div>
                                                        <h5>Strengths</h5>
                                                        <ul>
                                                            {evaluation.strengths.map((s) => (
                                                                <li key={s}>{s}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {evaluation.risks?.length > 0 && (
                                                    <div>
                                                        <h5>Risks</h5>
                                                        <ul>
                                                            {evaluation.risks.map((r) => (
                                                                <li key={r}>{r}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <form className="phone-screen-form" onSubmit={handleComplete}>
                            <p className="phone-screen-hint">
                                Completing with <strong>Pass</strong> moves the candidate to{' '}
                                <strong>Interviewing</strong>.
                            </p>
                            {meta.application_status && (
                                <p className="phone-screen-hint">
                                    Current application status: <code>{meta.application_status}</code>
                                    {locked ? ' · Completed' : ''}
                                    {onHold && !locked ? ' · On hold (editable)' : ''}
                                    {inviteSent && !locked ? ' · Invite sent' : ''}
                                    {scheduled && !locked ? ' · Scheduled' : ''}
                                </p>
                            )}

                            <div className="phone-screen-grid">
                                <label className="phone-screen-field">
                                    <span>Call date / time</span>
                                    <input
                                        type="datetime-local"
                                        value={form.call_at || ''}
                                        onChange={(e) => updateField('call_at', e.target.value)}
                                        disabled={locked || saving}
                                    />
                                </label>
                                <label className="phone-screen-field">
                                    <span>Recruiter name</span>
                                    <input
                                        type="text"
                                        value={form.recruiter_name}
                                        onChange={(e) => updateField('recruiter_name', e.target.value)}
                                        disabled={locked || saving}
                                        placeholder="Your name"
                                    />
                                </label>
                                <label className="phone-screen-field">
                                    <span>Duration (minutes)</span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={240}
                                        value={form.duration_minutes}
                                        onChange={(e) => updateField('duration_minutes', e.target.value)}
                                        disabled={locked || saving}
                                    />
                                </label>
                                <label className="phone-screen-field">
                                    <span>Score (0–100, optional)</span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={form.score}
                                        onChange={(e) => updateField('score', e.target.value)}
                                        disabled={locked || saving}
                                    />
                                </label>
                            </div>

                            <div className="phone-screen-grid">
                                <TriState
                                    label="Communication"
                                    value={form.checklist.communication}
                                    onChange={(v) => updateChecklist('communication', v)}
                                    disabled={locked || saving}
                                />
                                <TriState
                                    label="Role fit"
                                    value={form.checklist.role_fit}
                                    onChange={(v) => updateChecklist('role_fit', v)}
                                    disabled={locked || saving}
                                />
                                <label className="phone-screen-field">
                                    <span>Notice period</span>
                                    <input
                                        type="text"
                                        value={form.checklist.notice_period}
                                        onChange={(e) => updateChecklist('notice_period', e.target.value)}
                                        disabled={locked || saving}
                                    />
                                </label>
                                <label className="phone-screen-field">
                                    <span>Expected CTC</span>
                                    <input
                                        type="text"
                                        value={form.checklist.expected_ctc}
                                        onChange={(e) => updateChecklist('expected_ctc', e.target.value)}
                                        disabled={locked || saving}
                                    />
                                </label>
                                <label className="phone-screen-field">
                                    <span>Availability</span>
                                    <input
                                        type="text"
                                        value={form.checklist.availability}
                                        onChange={(e) => updateChecklist('availability', e.target.value)}
                                        disabled={locked || saving}
                                    />
                                </label>
                            </div>

                            <label className="phone-screen-field">
                                <span>Result *</span>
                                <select
                                    value={form.result}
                                    onChange={(e) => updateField('result', e.target.value)}
                                    disabled={locked || saving}
                                    required
                                >
                                    <option value="pass">Recruiter screening passed</option>
                                    <option value="hold">Hold → Stay in Recruiter screening</option>
                                    <option value="fail">Fail → Not selected</option>
                                </select>
                            </label>

                            <label className="phone-screen-field">
                                <span>Notes</span>
                                <textarea
                                    rows={4}
                                    value={form.notes}
                                    onChange={(e) => updateField('notes', e.target.value)}
                                    disabled={locked || saving}
                                    placeholder="Summary of the phone screen…"
                                />
                            </label>

                            <div className="phone-screen-actions">
                                <button type="button" className="phone-screen-btn secondary" onClick={onClose} disabled={saving}>
                                    Close
                                </button>
                                {!locked && (
                                    <>
                                        <button
                                            type="button"
                                            className="phone-screen-btn secondary"
                                            onClick={handleSaveDraft}
                                            disabled={saving}
                                        >
                                            {saving ? 'Saving…' : 'Save draft'}
                                        </button>
                                        <button type="submit" className="phone-screen-btn primary" disabled={saving}>
                                            {saving ? 'Saving…' : 'Complete recruiter screening'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </form>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PhoneScreeningModal;
